import { call, put, takeLatest, select, all, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { workInstructionsApi, WorkInstruction, CreateWorkInstructionRequest, UpdateWorkInstructionRequest } from '../../services/workInstructionsApi';
import {
  // Work Instructions actions
  fetchWorkInstructionsStart,
  fetchWorkInstructionsSuccess,
  fetchWorkInstructionsFailure,
  setWorkInstructionValidation,
  setWorkInstructionFilters,
  setWorkInstructionPagination,
  setWorkInstructionStatistics,

  // Document actions
  setDocumentUploadStart,
  setDocumentUploadProgress,
  setDocumentUploadSuccess,
  setDocumentUploadFailure,
  setDocumentDownloadError,

  // UI actions
  setWorkInstructionDialogOpen,
} from '../reducers/workInstructionsSlice';
import { RootState } from '../index';

// Work Instructions Sagas
function* fetchWorkInstructionsApi(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { pagination, filters } = state.workInstructions.workInstructions;
    
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      search: filters.search || undefined,
      active: filters.active,
      hasDocument: filters.hasDocument,
    };

    const response = yield call(workInstructionsApi.getAll, params);
    
    yield put(fetchWorkInstructionsSuccess({
      workInstructions: response.data,
      total: response.total,
      page: response.page,
    }));

    // Fetch statistics
    const stats = yield call(workInstructionsApi.getStatistics);
    yield put(setWorkInstructionStatistics(stats));
  } catch (error: any) {
    yield put(fetchWorkInstructionsFailure(error.message || 'Failed to fetch work instructions'));
  }
}

function* createWorkInstructionApi(action: PayloadAction<CreateWorkInstructionRequest>): Generator<any, void, any> {
  try {
    // Validate work instruction data
    const errors = workInstructionsApi.validation.validateWorkInstruction(action.payload);
    if (errors.length > 0) {
      const validation = errors.reduce((acc, error, index) => {
        acc[`field_${index}`] = error;
        return acc;
      }, {} as Record<string, string>);
      yield put(setWorkInstructionValidation(validation));
      return;
    }

    // Process the form data
    const processedData = workInstructionsApi.validation.prepareForSave(action.payload);
    
    const newWorkInstruction = yield call(workInstructionsApi.create, processedData as CreateWorkInstructionRequest);
    yield put(setWorkInstructionDialogOpen(false));
    yield call(fetchWorkInstructionsApi); // Refresh the list
  } catch (error: any) {
    yield put(setWorkInstructionValidation({ general: error.message || 'Failed to create work instruction' }));
  }
}

function* updateWorkInstructionApi(action: PayloadAction<UpdateWorkInstructionRequest>): Generator<any, void, any> {
  try {
    // Validate work instruction data
    const errors = workInstructionsApi.validation.validateWorkInstruction(action.payload);
    if (errors.length > 0) {
      const validation = errors.reduce((acc, error, index) => {
        acc[`field_${index}`] = error;
        return acc;
      }, {} as Record<string, string>);
      yield put(setWorkInstructionValidation(validation));
      return;
    }

    // Process the form data
    const processedData = workInstructionsApi.validation.prepareForSave(action.payload);

    const updatedWorkInstruction = yield call(workInstructionsApi.update, action.payload.Id, processedData as UpdateWorkInstructionRequest);
    yield put(setWorkInstructionDialogOpen(false));
    yield call(fetchWorkInstructionsApi); // Refresh the list
  } catch (error: any) {
    yield put(setWorkInstructionValidation({ general: error.message || 'Failed to update work instruction' }));
  }
}

function* deleteWorkInstructionApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(workInstructionsApi.delete, action.payload);
    yield call(fetchWorkInstructionsApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchWorkInstructionsFailure(error.message || 'Failed to delete work instruction'));
  }
}

function* bulkActivateWorkInstructionsApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    yield call(workInstructionsApi.bulkActivate, action.payload);
    yield call(fetchWorkInstructionsApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchWorkInstructionsFailure(error.message || 'Failed to activate work instructions'));
  }
}

function* bulkDeactivateWorkInstructionsApi(action: PayloadAction<string[]>): Generator<any, void, any> {
  try {
    yield call(workInstructionsApi.bulkDeactivate, action.payload);
    yield call(fetchWorkInstructionsApi); // Refresh the list
  } catch (error: any) {
    yield put(fetchWorkInstructionsFailure(error.message || 'Failed to deactivate work instructions'));
  }
}

function* exportWorkInstructionsApi(): Generator<any, void, any> {
  try {
    const blob = yield call(workInstructionsApi.export);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `work-instructions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    yield put(fetchWorkInstructionsFailure(error.message || 'Failed to export work instructions'));
  }
}

// Document Sagas
function* uploadDocumentApi(action: PayloadAction<File>): Generator<any, void, any> {
  try {
    yield put(setDocumentUploadStart());
    
    // Validate file
    const validationErrors = workInstructionsApi.validation.validateDocument(action.payload);
    if (validationErrors.length > 0) {
      yield put(setDocumentUploadFailure(validationErrors.join(', ')));
      return;
    }

    // Simulate upload progress
    for (let i = 10; i <= 90; i += 10) {
      yield put(setDocumentUploadProgress(i));
      yield call(() => new Promise(resolve => setTimeout(resolve, 100)));
    }

    const document = yield call(workInstructionsApi.uploadDocument, action.payload);
    yield put(setDocumentUploadSuccess(document));
  } catch (error: any) {
    yield put(setDocumentUploadFailure(error.message || 'Failed to upload document'));
  }
}

function* downloadDocumentApi(action: PayloadAction<{ documentId: string; fileName: string }>): Generator<any, void, any> {
  try {
    const blob = yield call(workInstructionsApi.downloadDocument, action.payload.documentId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = action.payload.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    yield put(setDocumentDownloadError(error.message || 'Failed to download document'));
  }
}

// Watcher Sagas
function* watchWorkInstructionFilters(): Generator<any, void, any> {
  yield takeLatest(setWorkInstructionFilters.type, function* () {
    yield call(fetchWorkInstructionsApi);
  });
}

function* watchWorkInstructionPagination(): Generator<any, void, any> {
  yield takeLatest(setWorkInstructionPagination.type, function* () {
    yield call(fetchWorkInstructionsApi);
  });
}

// Root Work Instructions Saga
export function* workInstructionsSaga(): Generator<any, void, any> {
  yield all([
    // Initialization
    takeLatest('workInstructions/fetchWorkInstructions', fetchWorkInstructionsApi),

    // Work Instructions operations
    takeEvery('workInstructions/createWorkInstruction', createWorkInstructionApi),
    takeEvery('workInstructions/updateWorkInstruction', updateWorkInstructionApi),
    takeEvery('workInstructions/deleteWorkInstruction', deleteWorkInstructionApi),
    takeEvery('workInstructions/bulkActivateWorkInstructions', bulkActivateWorkInstructionsApi),
    takeEvery('workInstructions/bulkDeactivateWorkInstructions', bulkDeactivateWorkInstructionsApi),
    takeEvery('workInstructions/exportWorkInstructions', exportWorkInstructionsApi),

    // Document operations
    takeEvery('workInstructions/uploadDocument', uploadDocumentApi),
    takeEvery('workInstructions/downloadDocument', downloadDocumentApi),

    // Watchers for automatic refetching
    watchWorkInstructionFilters(),
    watchWorkInstructionPagination(),
  ]);
} 